import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import PackageSelect from 'ApplicationEntry/components/WorkFlow/ProductsRiders/PackageSelect';
import {BillingAgent} from 'ApplicationEntry/models/BillingAgent';
import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {Premium} from 'ApplicationEntry/models/Premium';
import {InvalidGroupAIP, ValidGroupAIP} from 'ApplicationEntry/models/ProductsRiders/AIP/GroupAIP';
import States from 'ApplicationEntry/models/States';
import {isNonEmptyString} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import './AIP.sass';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as AIPActions from 'ApplicationEntry/ducks/ProductsRiders/aip';

type AIPProps = {
  premium: Premium;
  aip: InvalidGroupAIP | ValidGroupAIP;
  caseDetails: CaseDetails;
  billingAgent: BillingAgent;
};

const AIP = ({premium, caseDetails, aip, billingAgent}: AIPProps) => {
  const dispatch = useDispatch();
  const {
    planType,
    cwa,
    section125,
    units,
    calculatedPremium,
    indWeeklyPremium,
    familyWeeklyPremium,
    indMonthlyPremium,
    familyMonthlyPremium,
    situs,
    packageList,
    packageOption,
    jstamp,
  } = aip;

  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [situsLabel, setSitusLabel] = useState<HTMLLabelElement | null>();
  const [unitLabel, setUnitLabel] = useState<HTMLLabelElement | null>();

  const getPremium = (premium: Premium) => {
    if (premium.billMode.value === 'Monthly') {
      return premium.monthly.value;
    } else if (premium.billMode.value === 'Weekly') {
      return premium.weekly.value;
    } else if (premium.billMode.value === 'Bi-Weekly') {
      return premium.biWeekly.value;
    } else if (premium.billMode.value === 'Quarterly') {
      return premium.quarterly.value;
    } else if (premium.billMode.value === '28day') {
      return premium.twentyEightDays.value;
    } else if (premium.billMode.value === 'SemiMonthly') {
      return premium.semiMonthly.value;
    } else if (premium.billMode.value === 'SemiAnnual') {
      return premium.semiAnnual.value;
    } else if (premium.billMode.value === 'Ninthly') {
      return premium.ninthly.value;
    } else if (premium.billMode.value === 'Tenthly') {
      return premium.tenthly.value;
    } else if (premium.billMode.value === 'Thirteenthly') {
      return premium.thirteenthly.value;
    } else {
      return '0';
    }
  };

  useEffect(() => {
    const caseNum = matchCaseDetailsByType({
      Group: groupApp =>
        groupApp.caseNumber.key === 'Loaded'
          ? groupApp.caseNumber.Data
          : groupApp.caseNumber.key === 'UserAssignedCaseNumber'
          ? groupApp.caseNumber.value
          : '',
      _: () => '',
    })(caseDetails);

    dispatch(AIPActions.getPackages('AIP', caseNum));
  }, [caseDetails, dispatch]);

  const handlePremiumValues = () => {
    if (isNonEmptyString(billingAgent.billingMode)) {
      if (
        (packageOption.value === 'A' ||
          packageOption.value === 'B' ||
          packageOption.value === 'C' ||
          packageOption.value === 'D') &&
        planType.value === 'IND'
      ) {
        const premiumVal =
          billingAgent.billingMode.value === 'Weekly' ? indWeeklyPremium.value : indMonthlyPremium.value;

        const newPremiumValue = String(
          Number(
            Number(parseFloat(premiumVal).toFixed(2)) * (billingAgent.billingMode.value === 'Weekly' ? 52 : 12)
          ).toFixed(2)
        );
        dispatch(AIPActions.changePremium(parseFloat(premiumVal).toFixed(2)));
        dispatch(AIPActions.changeCalculatedPremium(newPremiumValue));
      } else if (
        (packageOption.value === 'A' ||
          packageOption.value === 'B' ||
          packageOption.value === 'C' ||
          packageOption.value === 'D') &&
        planType.value === 'FAMILY'
      ) {
        const premiumVal =
          billingAgent.billingMode.value === 'Weekly' ? familyWeeklyPremium.value : familyMonthlyPremium.value;

        const newPremiumValue = String(
          Number(
            Number(parseFloat(premiumVal).toFixed(2)) * (billingAgent.billingMode.value === 'Weekly' ? 52 : 12)
          ).toFixed(2)
        );

        dispatch(AIPActions.changePremium(parseFloat(premiumVal).toFixed(2)));
        dispatch(AIPActions.changeCalculatedPremium(newPremiumValue));
      } else if (calculatedPremium.key === 'Empty') {
        dispatch(AIPActions.changePremium(getPremium(premium)));
        dispatch(AIPActions.changeCalculatedPremium(getPremium(premium)));
      }
    }
  };

  useEffect(() => {
    handlePremiumValues();
  }, [packageOption.value, planType.value]);

  return (
    <div className="aip-section" id="aip-section">
      <form noValidate autoComplete="off" className="aip-section__form">
        <div className="aip-section__form-row">
          <Grid container spacing={4}>
            <PackageSelect
              packageName={packageOption}
              packageList={packageList}
              changePackageAction={AIPActions.changePackage}
              handlePackageItems={(packageItem: object) => {
                dispatch(AIPActions.changeUnits('1'));
                dispatch(AIPActions.changeIndMonthlyPremium(packageItem['individualMonthlyPremium']));
                dispatch(AIPActions.changeIndWeeklyPremium(packageItem['indWeeklyPremium']));
                dispatch(AIPActions.changeFamilyMonthlyPremium(packageItem['familyMonthlyPremium']));
                dispatch(AIPActions.changeFamilyWeeklyPremium(packageItem['famWeeklyPremium']));
              }}
            />
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="aip-section__form-text-field">
                <InputLabel ref={setPlanTypeLabel} id="aip-plantype-label">
                  Plan Type
                </InputLabel>
                <Select
                  labelId="aip-label-plantype"
                  labelWidth={planTypeLabel?.clientWidth}
                  id="aip-plantype"
                  value={planType.key === 'Empty' ? '' : planType.value}
                  onChange={e => {
                    dispatch(AIPActions.changePlanType(e.target.value as string));
                  }}
                  displayEmpty
                >
                  <MenuItem value="IND">Individual</MenuItem>
                  <MenuItem value="FAMILY">Family</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="aip-section__form-text-field">
                <InputLabel ref={setUnitLabel} id="aip-units-label">
                  Units
                </InputLabel>
                <Select
                  labelId="aip-label-units"
                  labelWidth={unitLabel?.clientWidth}
                  id="aip-units"
                  value={units.key === 'Empty' ? '' : units.value}
                  onChange={e => dispatch(AIPActions.changeUnits(e.target.value as string))}
                  displayEmpty
                  disabled
                >
                  <MenuItem value="1">1 Unit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="aip-section__form-text-field">
                <InputLabel ref={setSitusLabel} id="aip-situs-label">
                  Situs State
                </InputLabel>
                <Select
                  labelId="aip-label-situs"
                  labelWidth={situsLabel?.clientWidth}
                  id="aip-situs"
                  value={situs.key === 'Empty' ? '' : situs.value}
                  onChange={e => {
                    dispatch(BAActions.changeState(e.target.value as string));
                    dispatch(AIPActions.changeSitus(e.target.value as string));
                  }}
                  displayEmpty
                >
                  {States.map(stateCode => (
                    <MenuItem key={stateCode} value={stateCode}>
                      {stateCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="aip-premium"
                label="Premium"
                variant="outlined"
                placeholder="Premium"
                margin="normal"
                fullWidth
                className="aip-section__form-text-field"
                value={aip.premium.key === 'Empty' ? '' : aip.premium.value}
                onChange={event => {
                  dispatch(AIPActions.changePremium(parseFloat(event.target.value).toFixed(2) as string));
                  dispatch(AIPActions.changeCalculatedPremium(parseFloat(event.target.value).toFixed(2) as string));
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="aip-cwa"
                label="CWA"
                variant="outlined"
                placeholder="CWA"
                margin="normal"
                fullWidth
                className="aip-section__form-text-field"
                value={cwa.key === 'Empty' ? '' : cwa.value}
                onChange={e => dispatch(AIPActions.changeCWA(e.target.value as string))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkedB"
                    color="secondary"
                    id="aip-section125"
                    checked={section125}
                    onChange={() => dispatch(AIPActions.changeSection125(!section125))}
                  />
                }
                label={<Typography>Section 125</Typography>}
                className="aip-section__form-text-field"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkedB"
                    color="secondary"
                    id="aip-section125"
                    checked={jstamp}
                    disabled
                    onChange={() => dispatch(AIPActions.changeJStamp(!jstamp))}
                  />
                }
                label={<Typography>J-Stamp</Typography>}
                className="aip-section__form-text-field"
              />
            </Grid>
          </Grid>
        </div>
      </form>
    </div>
  );
};

export default AIP;
